import type { TLessonBase, TSpaceBase } from '@onesource/schemas';
import type { TLessonWithBlocks } from '@onesource/services';
import { stripHtml } from '@onesource/utils';

import { NavBreadcrumbCurrent, NavBreadcrumbLabel, NavBreadcrumbLink, NavBreadcrumbs } from '../nav-breadcrumbs';
import { StickyNavRow } from '../sticky-nav-row';

export interface LessonBreadcrumbsProps {
  spaceId: string;
  moduleId: string;
  lessonId: string;
  module?: TSpaceBase | null;
  lesson?: TLessonWithBlocks | null;
  parentLesson?: TLessonBase | null;
  currentLabel: string;
  // Slug mode: when all three are provided, links target the `/programs/...`
  // slug URLs; otherwise the legacy `/community-hub/spaces/...` UUID links are
  // used (kept for the single-lesson viewers that still render on legacy routes).
  programSlug?: string;
  moduleSlug?: string;
  lessonSlug?: string;
}

/**
 * Breadcrumbs for lesson content viewers (video / pdf / quiz).
 *
 * In slug mode (programSlug + moduleSlug + lessonSlug present) it renders the
 * `Program > Module > (ParentLesson) > Lesson` chain against the slug routes. In
 * legacy mode it renders UUID links, and when `moduleId === spaceId` (single-
 * lesson space) it renders a single link back to `/community-hub/spaces/:id/lesson`.
 *
 * Rendered inside `StickyNavRow`, so the trail stays pinned under the site
 * header on desktop and collapses to a "Back to <lesson>" button on mobile.
 */
export function LessonBreadcrumbs({
  spaceId,
  moduleId,
  lessonId,
  module,
  lesson,
  parentLesson,
  currentLabel,
  programSlug,
  moduleSlug,
  lessonSlug,
}: LessonBreadcrumbsProps) {
  const useSlug = !!(programSlug && moduleSlug && lessonSlug);

  if (useSlug) {
    const backHref = `/programs/${programSlug}/${moduleSlug}/${lessonSlug}`;
    const backLabel = stripHtml(lesson?.title) || undefined;
    return (
      <StickyNavRow backHref={backHref} backLabel={backLabel}>
        <NavBreadcrumbs>
          {module?.parent && (
            <NavBreadcrumbLink to="/programs/$programSlug" params={{ programSlug: programSlug! }}>
              <NavBreadcrumbLabel html={module.parent.name} />
            </NavBreadcrumbLink>
          )}
          {module && (
            <NavBreadcrumbLink
              to="/programs/$programSlug/$moduleSlug"
              params={{ programSlug: programSlug!, moduleSlug: moduleSlug! }}
            >
              <NavBreadcrumbLabel html={module.name} />
            </NavBreadcrumbLink>
          )}
          {parentLesson && (
            <NavBreadcrumbLink
              to="/programs/$programSlug/$moduleSlug"
              params={{ programSlug: programSlug!, moduleSlug: moduleSlug! }}
              search={{ sectionId: parentLesson.id }}
            >
              {parentLesson.title}
            </NavBreadcrumbLink>
          )}
          {lesson && (
            <NavBreadcrumbLink
              to="/programs/$programSlug/$moduleSlug/$lessonSlug"
              params={{ programSlug: programSlug!, moduleSlug: moduleSlug!, lessonSlug: lessonSlug! }}
            >
              <NavBreadcrumbLabel html={lesson.title} />
            </NavBreadcrumbLink>
          )}
          <NavBreadcrumbCurrent>{currentLabel}</NavBreadcrumbCurrent>
        </NavBreadcrumbs>
      </StickyNavRow>
    );
  }

  // Single-lesson slug viewer: the program home IS the lesson, so the trail is
  // just Program > <current>. (programSlug present, but no module/lesson slug.)
  if (programSlug && !moduleSlug) {
    return (
      <StickyNavRow
        backHref={`/programs/${programSlug}`}
        backLabel={stripHtml(module?.name || lesson?.title) || undefined}
      >
        <NavBreadcrumbs>
          <NavBreadcrumbLink to="/programs/$programSlug" params={{ programSlug }}>
            <NavBreadcrumbLabel html={module?.name || lesson?.title || ''} />
          </NavBreadcrumbLink>
          <NavBreadcrumbCurrent>{currentLabel}</NavBreadcrumbCurrent>
        </NavBreadcrumbs>
      </StickyNavRow>
    );
  }

  const isSingleLesson = moduleId === spaceId;

  // Mobile back target: the page this content block was opened from.
  const backHref = isSingleLesson
    ? `/community-hub/spaces/${spaceId}/lesson`
    : `/community-hub/spaces/${spaceId}/modules/${moduleId}/lessons/${lessonId}`;
  const backLabel = stripHtml(isSingleLesson ? module?.name : lesson?.title) || undefined;

  return (
    <StickyNavRow backHref={backHref} backLabel={backLabel}>
      <NavBreadcrumbs>
        {isSingleLesson && module && (
          <NavBreadcrumbLink to="/community-hub/spaces/$id/lesson" params={{ id: spaceId }}>
            <NavBreadcrumbLabel html={module.name} />
          </NavBreadcrumbLink>
        )}
        {!isSingleLesson && module?.parent && (
          <NavBreadcrumbLink to="/community-hub/spaces/$id" params={{ id: spaceId }}>
            <NavBreadcrumbLabel html={module.parent.name} />
          </NavBreadcrumbLink>
        )}
        {!isSingleLesson && module && (
          <NavBreadcrumbLink to="/community-hub/spaces/$id/modules/$moduleId" params={{ id: spaceId, moduleId }}>
            <NavBreadcrumbLabel html={module.name} />
          </NavBreadcrumbLink>
        )}
        {!isSingleLesson && parentLesson && (
          <NavBreadcrumbLink to="/community-hub/spaces/$id/modules/$moduleId" params={{ id: spaceId, moduleId }}>
            {parentLesson.title}
          </NavBreadcrumbLink>
        )}
        {!isSingleLesson && lesson && (
          <NavBreadcrumbLink
            to="/community-hub/spaces/$id/modules/$moduleId/lessons/$lessonId"
            params={{ id: spaceId, moduleId, lessonId }}
          >
            <NavBreadcrumbLabel html={lesson.title} />
          </NavBreadcrumbLink>
        )}
        <NavBreadcrumbCurrent>{currentLabel}</NavBreadcrumbCurrent>
      </NavBreadcrumbs>
    </StickyNavRow>
  );
}
